/**
 * Real user monitoring, off unless configured.
 *
 * Nothing leaves the browser until VUE_APP_RC_FARO_URL is set - the collector
 * URL from the Faro app in Grafana Cloud's Frontend Observability, which
 * carries the app's key in its own path, so there is nothing else to supply -
 * and never in development, whatever is set. With RUM off every call here is a
 * no-op and the SDK is not even downloaded: it lives in its own webpack chunk
 * behind the dynamic import below.
 *
 * Pages do not talk to this file. They use the useRum() composable, which is
 * safe to call whether or not this ever started.
 */

// Matches the app name in Grafana, so the two cannot drift apart.
import { scrubUrl } from './scrubUrl.js';
import { isNoisyFaroItem } from '@/plugins/telemetryNoise';

// Matches the app name in Grafana, so the two cannot drift apart.
const APP_NAME = 'ReadyChek_Web';

/** Resolves once the SDK has loaded, or to null when RUM is off. */
let faroPromise = null;

/** The live Faro instance, or null. Set after faroPromise resolves. */
let faro = null;

const config = () => {
  return {
    url: process.env.VUE_APP_RC_FARO_URL,
    version: process.env.VUE_APP_RC_VERSION,
    api: process.env.VUE_APP_RC_API,
    build: process.env.NODE_ENV,
  };
};

/**
 * RUM is off in development, whatever the settings say. Sessions are billed,
 * and a developer reloading a page all afternoon would be charged for as a
 * real fleet manager while making the real numbers meaningless. The
 * development env file carries no Faro settings; these are the second and
 * third locks - `vue-cli-service serve` is refused whatever it points at, and
 * so is any build talking to a localhost API.
 */
const isEnabled = () => {
  const values = config();
  if (!values.url) {
    return false;
  }
  if (values.build === 'development') {
    return false;
  }
  return environment(values.api) !== 'development';
};

/**
 * Which deployment this browser is talking to, taken from the API host so it
 * cannot disagree with where the data actually goes.
 */
const environment = (api) => {
  if (!api) {
    return 'unknown';
  }
  if (api.includes('localhost')) {
    return 'development';
  }
  if (api.includes('readychek.cloud')) {
    return 'cloud';
  }
  return 'production';
};

/**
 * Faro hands every outgoing item through here. Anything carrying a URL gets
 * scrubbed; anything we do not recognise is passed along untouched.
 */
const scrubItem = (item) => {
  try {
    const payload = item && item.payload;
    if (!payload) {
      return item;
    }
    // Browser noise never reaches Faro, on the same list PostHog uses.
    if (isNoisyFaroItem(item)) {
      return null;
    }
    if (payload.url) {
      payload.url = scrubUrl(payload.url);
    }
    if (payload.trace && payload.trace.url) {
      payload.trace.url = scrubUrl(payload.trace.url);
    }
    if (payload.context && payload.context.url) {
      payload.context.url = scrubUrl(payload.context.url);
    }
    if (payload.attributes && payload.attributes.url) {
      payload.attributes.url = scrubUrl(payload.attributes.url);
    }
  } catch (e) {
    // A transform that throws would drop the item. Sending it unscrubbed is
    // worse than sending nothing, so drop it deliberately instead.
    return null;
  }
  return item;
};

/**
 * Starts RUM if it is configured. Safe to call more than once; safe to call
 * when it is switched off. Never throws - monitoring that breaks the portal it
 * is monitoring is worse than no monitoring.
 */
export const startRum = () => {
  if (faroPromise) {
    return faroPromise;
  }
  if (!isEnabled()) {
    faroPromise = Promise.resolve(null);
    return faroPromise;
  }

  const values = config();

  faroPromise = import(/* webpackChunkName: "rum" */ '@grafana/faro-web-sdk')
    .then((sdk) => {
      faro = sdk.initializeFaro({
        url: values.url,
        app: {
          name: APP_NAME,
          version: values.version,
          environment: environment(values.api),
        },
        instrumentations: sdk.getWebInstrumentations({ captureConsole: false }),
        beforeSend: scrubItem,
      });
      return faro;
    })
    .catch(() => {
      // Offline, blocked by an ad blocker, or a bad key. The portal carries on.
      faroPromise = Promise.resolve(null);
      return null;
    });

  return faroPromise;
};

/** The live instance once started, or null. Used by useRum(). */
export const rumInstance = () => faro;

export const rumEnabled = isEnabled;

export const rumScrub = scrubUrl;
